import React from "react"
import PropTypes from "prop-types"
import { useIntl } from "gatsby-plugin-react-intl"
import styled, { css } from "styled-components"
import { GatsbyImage } from "gatsby-plugin-image"

import Link from "./Link"
import ImageContentful from "./ImageContentful"

import { mixins, media } from "../styles"
import ArrowRightSVG from "../images/icons/arrow-right.inline.svg"

const Header = styled.h3`
  ${mixins.title3}
`

const Subheader = styled.span`
  display: block;
  font-size: var(--font-size-15);
  font-weight: 700;
  margin: 15px 0 10px;
`

const TeaserContainer = styled(Link)`
  color: var(--color-spaceblue);
  display: block;
  text-decoration: none;
  transition: color var(--transitionTime);

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  ${(props) =>
    props.$compact &&
    css`
      ${Header} {
        ${mixins.title6}
      }

      ${Image} {
        margin-bottom: 10px;
      }
    `}

  &:hover {
    color: var(--color-sun);
  }
`

const Description = styled.div`
  ${mixins.copy4}

  p {
    margin: 0;
  }
`

const Image = styled.div`
  background: var(--color-spaceblue);
  margin: 0 0 30px;
  position: relative;

  [data-gatsby-image-wrapper] {
    opacity: 0.6;
    z-index: 0;
  }
`

const ExternalImage = styled(Image)`
  aspect-ratio: 1.6571428571;
  overflow: hidden;

  img {
    height: 100%;
    object-fit: cover;
    object-position: top;
    opacity: 0.6;
    width: 100%;
    z-index: 0;
  }
`

const ImageIcon = styled.div`
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  position: absolute;
  width: 100%;
  z-index: 1;
`

const Details = styled.div`
  @media ${media.md} {
    max-width: 85%;
  }
`

const ReadMore = styled.span`
  ${mixins.copy4}
  align-items: center;
  display: flex;
  margin-top: 20px;

  @media ${media.xl} {
    line-height: 1.1;
  }

  span {
    color: var(--color-sun);
    margin-left: 10px;
    height: 20px;
    width: 20px;
  }
`

const Teaser = ({
  arrowText,
  header,
  subheader,
  externalImage,
  headerLevel = 2,
  image,
  imageIcon,
  localImage,
  text,
  to,
  readMore,
  compact,
  ...props
}) => {
  const intl = useIntl()
  return (
    <TeaserContainer to={to} $compact={compact} {...props}>
      {image ? (
        <Image aria-hidden="true">
          {imageIcon ? <ImageIcon>{imageIcon}</ImageIcon> : null}
          <ImageContentful image={image} alt={header} />
        </Image>
      ) : null}
      {localImage ? (
        <Image aria-hidden="true">
          {imageIcon ? <ImageIcon>{imageIcon}</ImageIcon> : null}
          <GatsbyImage image={localImage} alt={header} />
        </Image>
      ) : null}
      {externalImage ? (
        <ExternalImage aria-hidden="true">
          {imageIcon ? <ImageIcon>{imageIcon}</ImageIcon> : null}
          <img src={externalImage} alt={header} />
        </ExternalImage>
      ) : null}
      <Details>
        {subheader && <Subheader>{subheader}</Subheader>}
        {header && <Header as={`h${headerLevel}`}>{header}</Header>}
        {text && (
          <Description>
            <p>{text}</p>
          </Description>
        )}
        {readMore ? (
          <ReadMore>
            {arrowText || intl.formatMessage({ id: "articles.readMore" })}
            <span>
              <ArrowRightSVG aria-hidden="true" />
            </span>
          </ReadMore>
        ) : null}
      </Details>
    </TeaserContainer>
  )
}

Teaser.propTypes = {
  arrowText: PropTypes.string,
  compact: PropTypes.bool,
  header: PropTypes.string,
  headerLevel: PropTypes.oneOf([2, 3, 4]),
  subheader: PropTypes.string,
  externalImage: PropTypes.string,
  image: PropTypes.object,
  imageIcon: PropTypes.object,
  readMore: PropTypes.bool,
  text: PropTypes.string,
  to: PropTypes.string.isRequired,
}

export default Teaser
